import {
    createContext,
    type PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { LoyaltySummary } from '@/types/loyalty';

function normalizeLoyaltySummary(summary: LoyaltySummary | null): LoyaltySummary | null {
    if (!summary) {
        return null;
    }

    const { loyaltyTiers } = summary;
    if (loyaltyTiers instanceof Map) {
        return { ...summary, loyaltyTiers: Object.fromEntries(loyaltyTiers) };
    }

    return summary;
}

/* eslint-disable react-refresh/only-export-components */

type LoyaltyContextValue = {
    loyalty: LoyaltySummary | null;
    isLoading: boolean;
    setLoyalty: (loyalty: LoyaltySummary | null) => void;
};

const defaultLoyaltyContextValue: LoyaltyContextValue = {
    loyalty: null,
    isLoading: false,
    setLoyalty: () => undefined,
};

const LoyaltyContext = createContext<LoyaltyContextValue>(defaultLoyaltyContextValue);

type LoyaltyProviderInitialSummary = Promise<LoyaltySummary | null> | LoyaltySummary | null;

const LoyaltyProvider = ({
    children,
    initialSummary,
}: PropsWithChildren<{ initialSummary?: LoyaltyProviderInitialSummary }>) => {
    const localUpdateRef = useRef(false);
    const [loyalty, setLoyaltyState] = useState<LoyaltySummary | null>(() =>
        initialSummary instanceof Promise || initialSummary === undefined
            ? null
            : normalizeLoyaltySummary(initialSummary)
    );
    const [isLoading, setIsLoading] = useState(() => initialSummary instanceof Promise);

    useEffect(() => {
        localUpdateRef.current = false;

        if (initialSummary === undefined) {
            setLoyaltyState(null);
            setIsLoading(false);
            return;
        }

        if (!(initialSummary instanceof Promise)) {
            setLoyaltyState(normalizeLoyaltySummary(initialSummary));
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        let active = true;
        void initialSummary
            .then((nextSummary) => {
                if (active && !localUpdateRef.current) {
                    setLoyaltyState(normalizeLoyaltySummary(nextSummary));
                }
            })
            .finally(() => {
                if (active) {
                    setIsLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [initialSummary]);

    const setLoyalty = useCallback((nextLoyalty: LoyaltySummary | null) => {
        localUpdateRef.current = true;
        setLoyaltyState(normalizeLoyaltySummary(nextLoyalty));
    }, []);

    const value = useMemo(
        () => ({
            loyalty,
            isLoading,
            setLoyalty,
        }),
        [loyalty, isLoading, setLoyalty]
    );

    return <LoyaltyContext.Provider value={value}>{children}</LoyaltyContext.Provider>;
};

export const useLoyaltyContext = (): LoyaltyContextValue => {
    return useContext(LoyaltyContext);
};

export const useLoyalty = (): LoyaltySummary | null => {
    return useLoyaltyContext().loyalty;
};

export const useLoyaltyLoading = (): boolean => {
    return useLoyaltyContext().isLoading;
};

export const useLoyaltyUpdater = (): ((loyalty: LoyaltySummary | null) => void) => {
    return useLoyaltyContext().setLoyalty;
};

export default LoyaltyProvider;
